import { useEffect, useState } from 'react'
import 'project/polyfill'
import Link from 'next/link'

import SeoContainer, {
  SeoContainerProps,
} from 'components/util/containers/SeoContainer'
import { sdk } from 'common/sdk'
import Strings from 'project/localisation'
import usePreview from 'project/usePreview'
import OffersByTag from 'components/marketplace/OffersByTag'
import PartnerHeroBanner from 'components/hero/PartnerHeroBanner'
import { NextPageWithLayout } from '../../types/nextPageWithLayout'
import PageContainer from 'components/util/containers/PageContainer'
import StaffPicksOffers from 'components/marketplace/StaffPicksOffers'
import { homeBreadcrumb } from 'components/util/containers/Breadcrumbs'
import { marketplaceStrings } from 'common/strings/marketplace-strings'
import { OfferSummary, Tag } from 'common/types/responses'
import imageResize from 'project/image-proxy-web'

type GroupedOffer = {
  tagId: string | null
  offers: OfferSummary[]
}

export type MarketplacePage = {
  _tags: Tag[]
  _offerGroup: GroupedOffer[]
  _featuredOffers?: OfferSummary[]
}
const MarketplacePage: NextPageWithLayout<MarketplacePage> = ({
  _featuredOffers,
  _offerGroup,
  _tags,
}) => {
  const [featuredOffers, setFeaturedOffers] = useState(_featuredOffers)
  const [offerGroup, setOfferGroup] = useState(_offerGroup)
  const [tags, setTags] = useState(_tags)
  const { preview } = usePreview()
  useEffect(() => {
    if (preview) {
      getData().then(({ featuredOffers, offerGroup, tags }) => {
        setFeaturedOffers(featuredOffers)
        setTags(tags)
        setOfferGroup(offerGroup)
      })
    }
  }, [preview])
  const seoProps: SeoContainerProps['seoProps'] = {
    description: marketplaceStrings.seoDescription,
    openGraph: {
      description: marketplaceStrings.seoDescription,
      images: [
        {
          alt: 'Hoxton Mix',
          height: 226,
          url: imageResize(
            require('../../public/images/homepage-hoxton-mix-team-desktop.png'),
            400,
            226,
          ),
          width: 400,
        },
      ],
      site_name: 'Hoxton Mix',
      title: marketplaceStrings.seoTitle,
      type: 'website',
      url: 'https://www.hoxtonmix.com/marketplace/',
    },
    title: marketplaceStrings.seoTitle,
    twitter: {
      cardType: 'summary_large_image',
    },
  }

  const breadcrumb = homeBreadcrumb().concat([
    {
      item: 'https://www.hoxtonmix.com/marketplace/',
      name: `${Strings.marketPlace}`,
      position: 2,
    },
  ])

  const handleFilter = (tagId: string) => {
    const tagSection = document.getElementById(tagId)
    if (tagSection) {
      tagSection.scrollIntoView({ behavior: 'smooth' })
    }
  }

  if (!tags?.length || !offerGroup?.length) {
    throw new Error(
      `Unexpected offers response from API. Tags: ${tags}. Offers: ${offerGroup}`,
    )
  }

  return (
    <SeoContainer breadcrumb={breadcrumb} seoProps={seoProps}>
      <div className='new-design'>
        <PartnerHeroBanner
          robotImage={{
            height: 635,
            src: '/images/new/robot-pointing-left.png',
            width: 787,
          }}
          robotImageMobile={{
            height: 506,
            src: '/images/new/robot-holding-mail.png',
            width: 392,
          }}
          title={
            <h1 className='fs-fluid-36-64 fw-bold mb-lg-3 mb-2 lh-114 text-center text-lg-start'>
              <span className='text-pink6'>Marketplace:</span> Business is
              better with the right apps & services
            </h1>
          }
          description={
            <div className='fs-fluid-24-38 fw-semibold text-pink6 text-center text-lg-start'>
              The Hoxton Mix works with a variety of amazing partners who offer
              products and services that could boost your business.
            </div>
          }
        />
        <div className='container marketplace mb-8 mb-md-22 px-lg-0'>
          {!!tags?.length && (
            <>
              <h4>{marketplaceStrings.popularCategories}</h4>
              <div className='category-container mt-4 gap-2'>
                {tags?.map((tag) => (
                  <button
                    className='chip'
                    key={tag.id}
                    onClick={() => handleFilter(tag.id)}
                  >
                    <span className='text-xs text-semibold'>{tag.name}</span>
                  </button>
                ))}
              </div>
            </>
          )}
          {!!featuredOffers?.length && (
            <StaffPicksOffers offers={featuredOffers} tags={tags} />
          )}
          {tags &&
            offerGroup?.map((group) => {
              const tag = tags.find((t) => t.id === group.tagId)
              if (tag) {
                return (
                  <OffersByTag offers={group.offers} tag={tag} key={tag.id} />
                )
              }
            })}
        </div>
        <div className='bg-black1 text-center py-6 text-white'>
          <h4 className='fs-fluid-24-32 mb-0'>
            <span className={'text-semibold'}>
              {marketplaceStrings.wantAFeatureOrService}
            </span>
            <Link className='fw-normal ms-6' href={'/marketplace-partner'}>
              {marketplaceStrings.becomeAPartner}
            </Link>
          </h4>
        </div>
        <div className='spacer'></div>
      </div>
    </SeoContainer>
  )
}

export default MarketplacePage

MarketplacePage.getLayout = (page) => (
  <PageContainer hideFooterCard>{page}</PageContainer>
)

// Prioritised brand patterns — first match wins, then alphabetical by brand name
const featuredOfferPriority: RegExp[] = [/tide/i]

const sortFeaturedOffers = (offers: OfferSummary[]): OfferSummary[] => {
  return [...offers].sort((a, b) => {
    const aName = a.brand?.name || a.title || ''
    const bName = b.brand?.name || b.title || ''
    const aPriority = featuredOfferPriority.findIndex((r) => r.test(aName))
    const bPriority = featuredOfferPriority.findIndex((r) => r.test(bName))
    const aRank = aPriority === -1 ? featuredOfferPriority.length : aPriority
    const bRank = bPriority === -1 ? featuredOfferPriority.length : bPriority
    if (aRank !== bRank) return aRank - bRank
    return aName.localeCompare(bName)
  })
}

const getData = async () => {
  const [tags, allOffersResponse, featuredResponse] = await Promise.all([
    sdk.getTags({ types: ['OFFER'] }),
    sdk.getAllOffers(),
    sdk.getAllOffers({ featured: true }),
  ])

  const allOffers = allOffersResponse?.results?.filter(Boolean) ?? []
  const featuredOffers = sortFeaturedOffers(
    featuredResponse?.results?.filter(Boolean) ?? [],
  )

  // Group offers by tag client-side
  const offerGroup = tags
    .map((tag) => ({
      offers: allOffers.filter((offer) => offer.tags?.includes(tag.id)),
      tagId: tag.id,
    }))
    .filter((group) => group.offers.length > 0)

  const activeTagIds = new Set(offerGroup.map((g) => g.tagId))
  const activeTags = tags.filter((tag) => activeTagIds.has(tag.id))

  return { featuredOffers, offerGroup, tags: activeTags }
}

export async function getStaticProps() {
  try {
    const { featuredOffers, offerGroup, tags } = await getData()
    return {
      props: {
        _featuredOffers: featuredOffers,
        _offerGroup: offerGroup,
        _tags: tags,
      },
      revalidate: 3600,
    }
  } catch (e) {
    console.error('Failed to fetch marketplace data:', e)
    throw e
  }
}
